import type { FC, ReactNode } from 'react'
import type { BakeryNow, MenuItem } from '@/types'

import Link from 'next/link'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber, formatPrice, toFaDigits } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { useBakeryClock } from '@/hooks/use-bakery-clock'
import { StatusTag } from '@/components/generals/status-tag'
import { SectionHeading } from '@/components/generals/section-heading'
import {
    formatClock,
    formatDateKey,
    getOpenStatus,
    getHoursFor,
    toMinutes
} from '@/lib/hours'

import { SHELL } from '@/constants/site'

interface TodaysBakeProps {
    items: Array<MenuItem>
    failed: boolean
    onSelect: (item: MenuItem) => void
}

const batchNote = (items: Array<MenuItem>, now: BakeryNow | null): ReactNode => {
    if (!now) return ''

    const upcoming = items
        .filter(item => item.nextBatchTime && toMinutes(item.nextBatchTime) > now.minutes)
        .sort((first, second) => toMinutes(first.nextBatchTime ?? '0:00') - toMinutes(second.nextBatchTime ?? '0:00'))[0]

    const status = getOpenStatus(now)
    const hours = getHoursFor(now.weekday)

    if (upcoming?.nextBatchTime && status.isOpen) return <>پخت بعدی: <span data-mdlfy-cms={`menu_items:${upcoming.id}:name`}>{upcoming.name}</span>، ساعت {formatClock(upcoming.nextBatchTime)} از تنور درمی‌آید.</>
    if (status.isOpen) return 'همه‌ی پخت امروز از تنور درآمده. تمام که شد، تمام شد.'
    if (hours?.open && now.minutes < toMinutes(hours.open)) return `اولین سینی‌ها ساعت ${formatClock(hours.open)} به پیشخوان می‌رسند.`

    return 'هر روز صبح، پیش از باز کردن در، همه‌چیز را تازه می‌پزیم.'
}

// A horizontal, ruled list of what is on the counter right now.
export const TodaysBake: FC<TodaysBakeProps> = ({ items, failed, onSelect }) => {
    const now = useBakeryClock()
    const railRef = useRef<HTMLUListElement>(null)
    const [canPrevious, setCanPrevious] = useState(false)
    const [canNext, setCanNext] = useState(false)

    const updateArrows = useCallback((): void => {
        const rail = railRef.current

        if (!rail) return

        // In right-to-left layout scrollLeft runs from 0 down into negative values.
        const offset = Math.abs(rail.scrollLeft)

        setCanPrevious(offset > 4)
        setCanNext(offset + rail.clientWidth < rail.scrollWidth - 4)
    }, [])

    useEffect(() => {
        const rail = railRef.current

        if (!rail) return

        updateArrows()
        rail.addEventListener('scroll', updateArrows, { passive: true })
        window.addEventListener('resize', updateArrows)

        return () => {
            rail.removeEventListener('scroll', updateArrows)
            window.removeEventListener('resize', updateArrows)
        }
    }, [updateArrows, items.length])

    const scrollRail = (direction: 1 | -1): void => {
        const rail = railRef.current

        if (!rail) return

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const reading = window.getComputedStyle(rail).direction === 'rtl' ? -1 : 1

        rail.scrollBy({ left: reading * direction * rail.clientWidth * 0.75, behavior: reduced ? 'auto' : 'smooth' })
    }

    const note = useMemo(() => batchNote(items, now), [items, now])
    const dateLabel = now ? formatDateKey(now.dateKey, { weekday: 'long', month: 'long', day: 'numeric' }) : null
    const available = items.filter(item => item.status !== 'sold-out').length

    return (
        <section id='todays-bake' aria-labelledby='todays-bake-title' className='py-20 lg:py-28'>
            <div className={SHELL}>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        id='todays-bake-title'
                        label={dateLabel ? `پخت امروز · ${dateLabel}` : 'پخت امروز'}
                        title={<>تازه از تنور، <span className='italic'>همین امروز صبح</span></>}
                    />

                    <div className='flex flex-col gap-5 lg:max-w-sm lg:items-end lg:text-end' data-reveal='up' style={revealDelay(200)}>
                        <p className='min-h-[1.5em] text-[0.9375rem] leading-relaxed text-crust-soft'>
                            {items.length > 0 && <span className='me-1 font-medium text-foreground'>{formatNumber(available)} از {formatNumber(items.length)} قلم هنوز موجود است.</span>}
                            {note}
                        </p>

                        {items.length > 3 && (
                            <div className='hidden gap-2 md:flex'>
                                <button
                                    type='button'
                                    onClick={() => scrollRail(-1)}
                                    disabled={!canPrevious}
                                    aria-label='نمایش موارد قبلی پخت امروز'
                                    className='inline-flex size-11 cursor-pointer items-center justify-center rounded-full border border-foreground/40 transition-colors duration-300 hover:border-foreground hover:bg-foreground hover:text-background focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber disabled:cursor-default disabled:opacity-30 disabled:hover:border-foreground/40 disabled:hover:bg-transparent disabled:hover:text-foreground'
                                >
                                    <ChevronLeft className='size-5 rtl:-scale-x-100' aria-hidden='true' />
                                </button>

                                <button
                                    type='button'
                                    onClick={() => scrollRail(1)}
                                    disabled={!canNext}
                                    aria-label='نمایش موارد بعدی پخت امروز'
                                    className='inline-flex size-11 cursor-pointer items-center justify-center rounded-full border border-foreground/40 transition-colors duration-300 hover:border-foreground hover:bg-foreground hover:text-background focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber disabled:cursor-default disabled:opacity-30 disabled:hover:border-foreground/40 disabled:hover:bg-transparent disabled:hover:text-foreground'
                                >
                                    <ChevronRight className='size-5 rtl:-scale-x-100' aria-hidden='true' />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {items.length === 0 ? (
                <div className={cn(SHELL, 'mt-12')}>
                    <div className='border-y border-line py-12 text-center' data-reveal='up'>
                        <p className='font-heading text-3xl leading-snug'>
                            {failed ? 'بارگذاری پخت امروز کمی طول کشیده.' : 'پخت امروز با بیرون آمدن اولین سینی‌ها همین‌جا می‌آید.'}
                        </p>

                        <p className='mx-auto mt-3 max-w-md text-crust-soft'>
                            {failed ? 'لطفاً یک دقیقه‌ی دیگر سر بزنید، یا تا آن موقع نگاهی به منوی کامل بیندازید.' : 'معمولاً تا ساعت ۷ صبح. تا آن موقع منوی کامل را ببینید، یا برای روزهای بعدی همین هفته پیش‌سفارش بدهید.'}
                        </p>

                        <Link href='/menu' className='link-underline mt-6 inline-flex items-center gap-2 text-sm font-medium'>
                            دیدن منوی کامل
                            <ArrowRight className='size-4 rtl:-scale-x-100' aria-hidden='true' />
                        </Link>
                    </div>
                </div>
            ) : (
                <div className='mt-12 border-y border-line lg:mt-16' data-reveal='fade' style={revealDelay(120)}>
                    <ul
                        ref={railRef}
                        aria-label='پخت امروز'
                        className='no-scrollbar flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain scroll-px-5 px-5 sm:scroll-px-8 sm:px-8 lg:scroll-px-[max(3rem,calc((100%-90rem)/2+3rem))] lg:px-[max(3rem,calc((100%-90rem)/2+3rem))]'
                    >
                        {items.map((item, index) => {
                            const soldOut = item.status === 'sold-out'
                            const batchLater = item.nextBatchTime && now && toMinutes(item.nextBatchTime) > now.minutes ? formatClock(item.nextBatchTime) : null

                            return (
                                <li
                                    key={item.id}
                                    className='group relative flex w-[16rem] shrink-0 snap-start flex-col border-s border-line px-6 py-7 transition-colors duration-500 first:border-s-0 first:ps-0 hover:bg-flour-deep/70 sm:w-[18rem] sm:first:border-s sm:first:ps-6'
                                >
                                    <div className={cn('flex flex-1 flex-col transition-opacity duration-500', soldOut && 'opacity-50')}>
                                        <div className='flex items-center justify-between gap-3'>
                                            <span className='label-caps text-crust-soft tabular-nums'>{toFaDigits(String(index + 1).padStart(2, '0'))}</span>
                                            {item.status && <StatusTag status={item.status} />}
                                        </div>

                                        <h3 className='mt-10 font-heading text-[1.75rem] leading-[1.3] font-normal'>
                                            <button
                                                type='button'
                                                onClick={() => onSelect(item)}
                                                className='cursor-pointer text-start after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-amber'
                                            >
                                                <span data-mdlfy-cms={`menu_items:${item.id}:name`}>{item.name}</span>
                                            </button>
                                        </h3>

                                        <p className='mt-3 line-clamp-2 text-sm leading-relaxed text-crust-soft' data-mdlfy-cms={`menu_items:${item.id}:description`}>{item.description}</p>

                                        <div className='mt-auto flex items-end justify-between gap-4 pt-8'>
                                            <span className='text-lg tabular-nums'>{formatPrice(item.price)}</span>

                                            {soldOut && batchLater && (
                                                <span className='text-xs text-crust-soft'>پخت بعدی {batchLater}</span>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            )}
        </section>
    )
}
